export const RECAPTURE_POLL_MS = 1500;

export const recapturePollingShouldStop = (status) =>
  status === "completed" || status === "failed";

export const isManualRecaptureRunning = (run) =>
  Boolean(run && run.trigger === "manual" && run.status === "running");

export const recaptureProgressPercent = (progress) => {
  const total = Number(progress && progress.total);
  const processed = Number(progress && progress.processed) || 0;
  if (!Number.isFinite(total) || total <= 0) return null;
  return Math.min(100, Math.round((processed / total) * 100));
};

export const recaptureScopeLabel = (progress = {}, fallback = "") => {
  const client = progress.current_client_name;
  const warehouse = progress.current_warehouse_name;
  if (client && warehouse) return `${client} → ${warehouse}`;
  if (client) return client;
  if (warehouse) return warehouse;
  return fallback;
};

export const recaptureHeadline = (run) => {
  const status = run && run.status;
  if (status === "completed") return "Recapture completed";
  if (status === "failed") return "Recapture failed";
  return "Recapture in progress";
};

export const recaptureSubmitDisabled = ({
  hasSelection,
  recapturing,
  snapshotInProgress,
} = {}) =>
  !hasSelection || Boolean(recapturing) || Boolean(snapshotInProgress);

export const recaptureShouldRefreshHealth = (run, lastRefreshedId) => {
  if (!run || run.status !== "completed") return false;
  const id = String(run.run_id || run._id || "");
  return Boolean(id) && id !== String(lastRefreshedId || "");
};

export const recaptureStatusMessage = (run) => {
  const progress = (run && run.progress) || {};
  if (run && run.status === "failed") {
    return run.error || progress.message || "Recapture failed.";
  }
  if (run && run.status === "completed") {
    return progress.message || "Snapshot successfully recaptured.";
  }
  if (progress.total == null && !(progress.processed > 0)) {
    return progress.message || "Preparing recapture...";
  }
  if (!(Number(progress.processed) > 0)) {
    return progress.message || "Loading current inventory...";
  }
  return progress.message || "Processing current inventory...";
};

/**
 * Poll fetchRun until the recapture is completed or failed.
 * Stops on unmount/cancel via the returned stop() function.
 */
export const startRecapturePolling = ({
  fetchRun,
  onUpdate,
  intervalMs = RECAPTURE_POLL_MS,
} = {}) => {
  if (typeof fetchRun !== "function") {
    throw new Error("fetchRun is required");
  }

  let stopped = false;
  let timer = null;

  const stop = () => {
    stopped = true;
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const tick = async () => {
    if (stopped) return;
    try {
      const run = await fetchRun();
      if (stopped) return;
      if (typeof onUpdate === "function") onUpdate(run);
      if (recapturePollingShouldStop(run && run.status)) {
        stop();
      }
    } catch (_error) {
      // A transient status fetch error is not a recapture failure. Keep
      // polling until the backend reports completed or failed.
      if (stopped) return;
    }
  };

  timer = setInterval(tick, intervalMs);
  tick();
  return { stop };
};
